import React, { useEffect, useContext, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { getToken } from "../../utils/tokenUtils";
import { AuthContext } from "../../contexts/AuthContext";
import * as XLSX from "xlsx";
import { getRoleBasedRoute } from "../../utils/routeUtils";

const AttendancePage = () => {
  const { user, roles = [] } = useContext(AuthContext);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [defaultMonth, setDefaultMonth] = useState("");
  const token = getToken();
  const employeeId = attendanceData?.employee_id || "";

  // console.log("attendanceData", attendanceData);

  const formatTo12Hour = (timeStr) => {
    if (!timeStr) return "-";
    const [hour, minute] = timeStr.split(":");
    const date = new Date();
    date.setHours(parseInt(hour), parseInt(minute));
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://hrmsnew.prepstripe.com/api/my-attendance",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = response.data;
      setAttendanceData(data);

      // Auto-select current month
      const today = new Date();
      const currentMonthStr = `${today.toLocaleString("default", { month: "long" })} ${today.getFullYear()}`;
      setDefaultMonth(currentMonthStr);

      const foundMonth = data.monthly_attendance?.find(
        (entry) => entry.month === currentMonthStr,
      );
      if (foundMonth) {
        setSelectedMonth(currentMonthStr);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
  }, [token]);

  const months =
    attendanceData?.monthly_attendance?.map((entry) => entry.month) || [];

  const selectedMonthData = attendanceData?.monthly_attendance?.find(
    (entry) => entry.month === selectedMonth,
  );

  const currentMonthAttendance = selectedMonthData?.attendance || [];

  const indicators = {
    A: currentMonthAttendance.filter((record) => record.da_type === "A").length,
    P: currentMonthAttendance.filter((record) => record.da_type === "P").length,
    WO: currentMonthAttendance.filter((record) => record.da_type === "WO")
      .length,
  };

  const handleExportToExcel = () => {
    if (!selectedMonthData?.attendance?.length) {
      alert("No data to export!");
      return;
    }

    const dataToExport = selectedMonthData.attendance.map((record) => ({
      Date: record.date_with_day,
      "In Time": formatTo12Hour(record.in_time),
      "Out Time": formatTo12Hour(record.out_time),
      "Total Hours": record.total_hours,
      "DA Type": record.da_type,
      Portion: record.portion,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, selectedMonth);

    XLSX.writeFile(
      workbook,
      `Attendance_${selectedMonth.replace(/\s/g, "_")}.xlsx`,
    );
  };

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  return (
    // mobile responsive
    <div className="p-4 sm:p-6 bg-white dark:bg-gray-900 rounded-lg">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex flex-wrap items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg
                className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="ms-1 text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">
                Attendance
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {selectedMonthData?.attendance.length > 0 && (
        <>
          <div className="mb-4">
            <div className="flex flex-col sm:flex-row sm:gap-10 gap-2 mb-5">
              <div className="text-base sm:text-lg dark:text-white font-semibold">
                Employee: <span className="text-blue-600">{user?.name}</span>
              </div>

              <div className="text-base dark:text-white sm:text-lg font-semibold">
                Employee Code:{" "}
                <span className="text-blue-600">{employeeId}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <label className="mr-2 font-medium dark:text-white">
                Filter by Month:
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border dark:bg-black dark:text-white dark:border-gray-700 p-2 rounded w-full sm:w-auto"
              >
                {months.map((month, idx) => (
                  <option key={idx} value={month}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}

      {/* Indicators + Buttons */}
      <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2 shadow-md py-1 px-3  rounded-md">
            <span className="w-4 h-4 bg-red-500 rounded-full"></span>
            <span className="dark:text-white">Absent: {indicators.A}</span>
          </div>
          <div className="flex items-center gap-2 shadow-md py-1 px-3  rounded-md">
            <span className="w-4 h-4 bg-green-500 rounded-full"></span>
            <span className="dark:text-white">Present: {indicators.P}</span>
          </div>
          <div className="flex items-center gap-2 shadow-md py-1 px-3  rounded-md">
            <span className="w-4 h-4 bg-yellow-500 rounded-full"></span>
            <span className="dark:text-white">Week Off: {indicators.WO}</span>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {selectedMonthData?.attendance.length > 0 && (
            <button
              onClick={() => setSelectedMonth(defaultMonth)}
              className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 w-full sm:w-auto"
            >
              Refresh
            </button>
          )}
          {selectedMonthData?.attendance?.length > 0 && (
            <button
              onClick={handleExportToExcel}
              className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-4 py-2 rounded w-full sm:w-auto"
            >
              Export to Excel
            </button>
          )}
        </div>
      </div>

      {/* {loading ? (
        <p>Loading attendance...</p>
      ) : selectedMonth && selectedMonthData ? ( */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg">
        <table className="w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Day
              </th>
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                In Time
              </th>
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Out Time
              </th>
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Total Hrs
              </th>
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Attendance Status
              </th>
              <th className="py-2 px-4 dark:text-white text-left font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Portion
              </th>
            </tr>
          </thead>
          <tbody>
            {selectedMonthData?.attendance &&
            selectedMonthData?.attendance.length > 0 ? (
              selectedMonthData?.attendance.map((record, idx) => (
                <tr
                  key={idx}
                  className="dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white dark:text-white"
                >
                  <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">
                    {record.date_with_day}
                  </td>
                  <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">
                    {formatTo12Hour(record.in_time)}
                  </td>
                  <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">
                    {formatTo12Hour(record.out_time)}
                  </td>
                  <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">
                    {record.total_hours}
                  </td>
                  <td
                    className={`py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap ${
                      record.da_type === "A"
                        ? "bg-red-500"
                        : record.da_type === "P"
                          ? "bg-green-500"
                          : "bg-yellow-500"
                    }`}
                  >
                    {record.da_type}
                  </td>
                  <td className="py-2 px-4 dark:text-white text-xs md:text-sm whitespace-nowrap">
                    {record.portion}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="text-center dark:text-white text-gray-500 p-4"
                >
                  No attendance data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* ) : (
        <p className="text-gray-500">Select a month to view attendance.</p>
      )} */}
    </div>
  );
};

export default AttendancePage;
